import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useGetUnitPlans } from '@apps-common/apis/portal/unit'
import { QUERY_GET_UNIT_ACTIVATION_TEMPLATES, useGetUnitActivationTemplateById } from '@apps-common/apis/portal/unit-activations'
import { useCreateUnitActivationTemplate } from '@apps-common/apis/portal/unit-activations/create-unit-activation-template'
import { useUpdateUnitActivationTemplate } from '@apps-common/apis/portal/unit-activations/update-unit-activation-template'
import {
  UnitActivationPlanCard,
  UnitActivationPlanCardSkeleton
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/components'
import { SECTION_TITLE_DEFAULT_CLASSES, SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm } from '@apps-common/components/elements'
import { CheckboxInputSM, INPUT_INFO_SM_NO_BORDER, InputInfoSM, InputSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import { CompanySelectModal, ModalButton } from '@apps-common/components/modals'
import { ModalBody, ModalCloseButton, ModalFooter, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { ModalContainer } from '@apps-common/components/modals/modal/ModalContainer'
import { CompanyType } from '@apps-common/config/company'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import { queryClient } from '@apps-common/lib'
import { ICompanyListModel } from '@apps-common/types/portal/company'
import { IActivationServiceRequestTemplateModel } from '@apps-common/types/portal/service-request'
import { IUnitPlanModel } from '@apps-common/types/portal/unit'

export interface TemplateModifyModalProps {
  templateId?: number
}

export const TemplateModifyModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: TemplateModifyModalProps
}) => {
  const { templateId } = componentProps
  const isEditing = !!templateId && !Number.isNaN(templateId)
  const user = usePortalFirebaseUser()
  const { isMonitoringCentre, isRadionetUser, company: companyName, companyId } = user || {}

  const {
    data: template,
    isLoading: templateIsLoading,
    isError: templateIsError
  } = useGetUnitActivationTemplateById({ config: { enabled: isEditing }, id: templateId })

  const { data: plans, isLoading: plansIsLoading, isError: plansIsError } = useGetUnitPlans({})

  const { mutate: create, isPending: isCreatePending } = useCreateUnitActivationTemplate(true, (model) => {
    queryClient.invalidateQueries({ queryKey: QUERY_GET_UNIT_ACTIVATION_TEMPLATES() })
    onDismiss(JSON.stringify(model), ModalDismiss.Confirm)
  })
  const { mutate: update, isPending: isUpdatePending } = useUpdateUnitActivationTemplate(true, (model) => {
    queryClient.invalidateQueries({ queryKey: QUERY_GET_UNIT_ACTIVATION_TEMPLATES() })
    onDismiss(JSON.stringify(model), ModalDismiss.Confirm)
  })

  const [mc, setMc] = useState<ICompanyListModel | undefined>()
  const [bureau, setBureau] = useState<ICompanyListModel | undefined>()
  const [tech, setTech] = useState<ICompanyListModel | undefined>()
  const [plan, setPlan] = useState<IUnitPlanModel | undefined>()

  useEffect(() => {
    if (!companyName || !companyId) {
      return
    }

    if (isRadionetUser) {
      return
    }

    if (isMonitoringCentre) {
      setMc({
        companyId,
        companyName
      })
    } else {
      setBureau({
        companyId,
        companyName
      })
    }
  }, [isMonitoringCentre, isRadionetUser, companyName, companyId])

  const {
    register,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<IActivationServiceRequestTemplateModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    register('unitPlanId', {
      validate: (value) => {
        if (!value || value.length === 0) {
          return 'Plan required'
        }

        return true
      }
    })
  })

  useEffect(() => {
    if (!templateIsLoading && !templateIsError && template) {
      reset(template)
    }
  }, [template, templateIsLoading, templateIsError])

  const mCentreNameVal = watch('mCentre')
  const bureauNameVal = watch('bureau')
  const techNameVal = watch('tech')
  const planIdVal = watch('unitPlanId')

  useEffect(() => {
    if (!mc && !mCentreNameVal) return

    setValue('mCentreId', mc?.companyId || '', { shouldValidate: true })
    setValue('mCentre', mc?.companyName || '', { shouldValidate: true })
  }, [mc])

  useEffect(() => {
    if (!bureau && !bureauNameVal) return

    setValue('assignBureau', !!bureau, { shouldValidate: true })
    setValue('bureauId', bureau?.companyId || '', { shouldValidate: true })
    setValue('bureau', bureau?.companyName || '', { shouldValidate: true })
  }, [bureau])

  useEffect(() => {
    if (!tech && !techNameVal) return

    setValue('assignTech', !!tech, { shouldValidate: true })
    setValue('techId', tech?.companyId || '', { shouldValidate: true })
    setValue('tech', tech?.companyName || '', { shouldValidate: true })
  }, [tech])

  useEffect(() => {
    if (!plan && !planIdVal) return

    setValue('unitPlanId', plan?.unitPlanId || '', { shouldValidate: true })
  }, [plan])

  useEffect(() => {
    if (planIdVal && planIdVal.length > 0 && plans && plans.length > 0 && plan?.unitPlanId !== planIdVal) {
      const selectedPlan = plans.find((p) => p.unitPlanId === planIdVal)

      if (selectedPlan) {
        setPlan(selectedPlan)
      } else {
        setPlan(undefined)
      }
    }
  }, [planIdVal, plans])

  const submit = (values: IActivationServiceRequestTemplateModel) => {
    if (isEditing) {
      update({ model: values })
    } else {
      create({ model: values })
    }
  }

  const templateLoading = isEditing && (templateIsLoading || templateIsError)
  const plansLoading = plansIsLoading || plansIsError
  const loading = templateLoading || plansLoading

  return (
    <ModalContainer>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle={isEditing ? 'Update' : 'Create'} title='Activation template' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='pt-[100px]'>
        <form noValidate id='unit-activation-template-modify-form' onSubmit={handleSubmit(submit)}>
          <div className='flex flex-col gap-4'>
            <div className='bg-surface border border-border px-4 py-4 rounded-2xl'>
              <InputSM
                type='text'
                placeholder='Enter a name to identify template'
                label='Template name'
                id='name'
                showLabel
                registration={register('name', {
                  required: FORM_DEFAULT_REQUIRED
                })}
                requiredAsterisk
                disabled={isSubmitting}
                loading={loading}
                errors={errors.name}
              />
            </div>

            <div className='bg-surface border border-border rounded-3xl px-4 pt-4 gap-[15px]'>
              <SectionTitle text='Company' classes='pl-2' />
              <VertSpacerMd />
              <div className='grid md:grid-cols-2 gap-x-4'>
                {(isRadionetUser || !isMonitoringCentre) && (
                  <InputInfoSM
                    id='mCentreId'
                    hideNoEditIcon
                    showLabel
                    label='Monitoring Centre'
                    value={
                      <div>
                        <ModalButton
                          classes='w-full'
                          component={CompanySelectModal}
                          componentProps={{
                            subtitle: 'Selection',
                            title: 'Monitoring Centre',
                            companyType: CompanyType.MonitoringCentre
                          }}
                          confirmCallback={(cy) => setMc(cy)}
                          disabled={loading || isSubmitting}
                        >
                          {(!mCentreNameVal || mCentreNameVal?.length === 0) && (
                            <ButtonRoundedSm
                              type='div'
                              text='Select Monitoring Centre'
                              loading={false}
                              icon={<commonIcons.add />}
                              iconRight
                              className='action-brand !h-[30px] md:max-w-[180px]'
                            />
                          )}
                          {mCentreNameVal && mCentreNameVal?.length > 0 && (
                            <span className='flex items-center justify-start gap-[5px]'>
                              {mCentreNameVal} <commonIcons.edit className='text-[16px]' />
                            </span>
                          )}
                        </ModalButton>
                      </div>
                    }
                    registration={register('mCentreId', {
                      required: FORM_DEFAULT_REQUIRED
                    })}
                    requiredAsterisk
                    loading={loading}
                    errors={errors?.mCentreId}
                    className={classNames(INPUT_INFO_SM_NO_BORDER, '!opacity-100')}
                  />
                )}
                {(isRadionetUser || isMonitoringCentre) && (
                  <InputInfoSM
                    id='bureauId'
                    hideNoEditIcon
                    showLabel
                    label='Bureau'
                    value={
                      <div>
                        <ModalButton
                          classes='w-full'
                          component={CompanySelectModal}
                          componentProps={{
                            subtitle: 'Selection',
                            title: 'Bureau',
                            companyType: CompanyType.Bureau
                          }}
                          confirmCallback={(cy) => setBureau(cy)}
                          disabled={loading || isSubmitting}
                        >
                          {(!bureauNameVal || bureauNameVal?.length === 0) && (
                            <ButtonRoundedSm
                              type='div'
                              text='Select Bureau'
                              loading={false}
                              icon={<commonIcons.add />}
                              iconRight
                              className='action-brand !h-[30px] md:max-w-[180px]'
                            />
                          )}
                          {bureauNameVal && bureauNameVal?.length > 0 && (
                            <span className='flex items-center justify-start gap-[5px]'>
                              {bureauNameVal} <commonIcons.edit className='text-[16px]' />
                            </span>
                          )}
                        </ModalButton>
                      </div>
                    }
                    registration={register('bureauId', {
                      required: false
                    })}
                    loading={loading}
                    errors={errors?.bureauId}
                    className={classNames(INPUT_INFO_SM_NO_BORDER, '!opacity-100')}
                  />
                )}
                <InputInfoSM
                  id='techId'
                  hideNoEditIcon
                  showLabel
                  label='Technician'
                  value={
                    <div>
                      <ModalButton
                        classes={classNames('w-full border-none text-secondary-content text-left')}
                        component={CompanySelectModal}
                        componentProps={{
                          subtitle: 'Selection',
                          title: 'Technician',
                          companyType: CompanyType.Technician
                        }}
                        confirmCallback={(cy) => setTech(cy)}
                        disabled={loading || isSubmitting}
                      >
                        {(!techNameVal || techNameVal?.length === 0) && (
                          <ButtonRoundedSm
                            type='div'
                            text='Select Technician'
                            loading={false}
                            icon={<commonIcons.add />}
                            iconRight
                            className='action-brand !h-[30px] md:max-w-[180px]'
                          />
                        )}
                        {techNameVal && techNameVal?.length > 0 && (
                          <span className='flex items-center justify-start gap-[5px]'>
                            {techNameVal} <commonIcons.edit className='text-[16px]' />
                          </span>
                        )}
                      </ModalButton>
                    </div>
                  }
                  registration={register('techId', {
                    required: false
                  })}
                  loading={loading}
                  errors={errors?.techId}
                  className={classNames(INPUT_INFO_SM_NO_BORDER, '!opacity-100')}
                />
              </div>
            </div>
            <div className='bg-surface border border-border rounded-3xl px-4 pt-4 gap-[15px]'>
              <SectionTitle text='Device' classes='pl-2' />
              <VertSpacerMd />
              <div className='grid grid-cols-1'>
                <CheckboxInputSM
                  label='Use line supervision'
                  id='useLineSupervision'
                  loading={loading}
                  disabled={loading || isSubmitting}
                  registration={register('useLineSupervision')}
                  errors={errors.useLineSupervision}
                />
              </div>
            </div>
            <div className='bg-surface border border-border rounded-3xl px-4 py-4 gap-[15px]'>
              <InputInfoSM
                id='unitPlanId'
                hideNoEditIcon
                showLabel
                label='Plan'
                value={
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-[10px]'>
                    <AnimatePresence>
                      {loading &&
                        Array.from(Array(6).keys()).map((k) => (
                          <motion.div key={`unit-plan-skeleton-${k}`} {...motionFadeIn(0.2)}>
                            <UnitActivationPlanCardSkeleton />
                          </motion.div>
                        ))}
                      {!loading &&
                        plans &&
                        plans?.map((p, i) => (
                          <motion.div key={`unit-plan-${p.unitPlanId}`} {...motionFadeIn(0.2)}>
                            <UnitActivationPlanCard
                              data={p}
                              planId={planIdVal}
                              // upgradePath={plan.}
                              onChange={(id) => setValue('unitPlanId', id, { shouldValidate: true })}
                            />
                          </motion.div>
                        ))}
                    </AnimatePresence>
                  </div>
                }
                registration={register('unitPlanId', {
                  required: FORM_DEFAULT_REQUIRED
                })}
                requiredAsterisk
                loading={false}
                errors={errors?.mCentreId}
                labelClassName={classNames(SECTION_TITLE_DEFAULT_CLASSES, 'mb-')}
                className='!cursor-default !opacity-100 !p-0 !border-none'
              />
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <ButtonRoundedSm
          type='submit'
          form='unit-activation-template-modify-form'
          loading={loading || isSubmitting}
          text='Save'
          className='action-success'
        />
      </ModalFooter>
    </ModalContainer>
  )
}
